import { useState, useCallback } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useDropzone } from 'react-dropzone'
import { format } from 'date-fns'
import { fr } from 'date-fns/locale'
import {
  CreditCard,
  Upload,
  Search,
  Filter,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Receipt,
  Calendar,
  User,
  Building,
  Eye,
  Edit,
  Trash2,
  Plus,
  AlertTriangle,
  CheckCircle,
  PieChart,
  BarChart3,
  Download,
  X,
  FileText,
  Zap
} from 'lucide-react'
import toast from 'react-hot-toast'
import { motion } from 'framer-motion'
import { PieChart as RechartsPieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend } from 'recharts'

import { useAuth } from '@/context/AuthContext'
import ExpenseSummaryImporter from '@/components/ExpenseSummaryImporter'
import ReimbursementTracker from '@/components/ReimbursementTracker'
import {
  getMedicalInvoices,
  createMedicalInvoice,
  updateMedicalInvoice,
  deleteMedicalInvoice,
  searchMedicalInvoices,
  getMonthlyExpenses,
  getExpensesBySpecialty,
  getTotalLifetimeExpenses,
  getUnpaidInvoices,
  extractInvoiceDataFromPDF,
  getSpecialtyLabel,
  getSpecialtyColor,
  getSpecialtyIcon,
  formatCurrency,
  calculateReimbursementRate,
  getInvoiceOutstandingAmount,
  getInvoiceReimbursedAmount,
  getInvoiceRemainingPatientCharge,
  getPaymentStatusColor,
  getPaymentStatusLabel,
  validateInvoiceInput
} from '@/lib/medical-invoices'
import { saveMedicalInvoiceWithReconciliation } from '@/lib/invoice-summary-import'
import type { MedicalInvoice, MedicalInvoiceInput, MedicalSpecialty } from '@/types/health'

interface InvoiceFormData {
  practitioner_name: string
  medical_facility: string
  specialty: MedicalSpecialty
  service_date: string
  invoice_date: string
  total_amount: number
  patient_paid_amount: number
  is_paid: boolean
  payment_date: string
  secu_reimbursed: number
  mutuelle_reimbursed: number
  invoice_number: string
  notes: string
}

const initialFormData: InvoiceFormData = {
  practitioner_name: '',
  medical_facility: '',
  specialty: 'general_medicine',
  service_date: new Date().toISOString().split('T')[0],
  invoice_date: new Date().toISOString().split('T')[0],
  total_amount: 0,
  patient_paid_amount: 0,
  is_paid: false,
  payment_date: '',
  secu_reimbursed: 0,
  mutuelle_reimbursed: 0,
  invoice_number: '',
  notes: ''
}

const specialties: { value: MedicalSpecialty; label: string }[] = [
  { value: 'general_medicine', label: 'Médecine générale' },
  { value: 'gastroenterology', label: 'Gastroentérologie' },
  { value: 'gynecology', label: 'Gynécologie' },
  { value: 'laboratory', label: 'Laboratoire' },
  { value: 'radiology', label: 'Radiologie' },
  { value: 'pharmacy', label: 'Pharmacie' },
  { value: 'physiotherapy', label: 'Kinésithérapie' },
  { value: 'psychology', label: 'Psychologie' },
  { value: 'dermatology', label: 'Dermatologie' },
  { value: 'cardiology', label: 'Cardiologie' },
  { value: 'endocrinology', label: 'Endocrinologie' },
  { value: 'rheumatology', label: 'Rhumatologie' },
  { value: 'neurology', label: 'Neurologie' },
  { value: 'surgery', label: 'Chirurgie' },
  { value: 'emergency', label: 'Urgences' },
  { value: 'other', label: 'Autre' }
]

const CHART_COLORS = [
  '#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6',
  '#06B6D4', '#84CC16', '#F97316', '#EC4899', '#6366F1'
]

function getInvoicePrimaryFacts(invoice: MedicalInvoice): string[] {
  return [
    getSpecialtyLabel(invoice.specialty),
    `Soin ${format(new Date(invoice.service_date), 'd MMM yyyy', { locale: fr })}`,
    invoice.medical_facility
  ].filter((value): value is string => Boolean(value))
}

function getInvoiceSourceState(invoice: MedicalInvoice) {
  const metadata =
    invoice.metadata && typeof invoice.metadata === 'object'
      ? (invoice.metadata as Record<string, unknown>)
      : {}

  const summaryImportCount =
    typeof metadata.summary_import_count === 'number' ? metadata.summary_import_count : 0
  const isSummaryOnly = metadata.is_summary_only === true && !invoice.document_url
  const hasRealInvoice = Boolean(invoice.document_url) || metadata.has_real_invoice === true || !isSummaryOnly
  const isReconciled = summaryImportCount > 0 && hasRealInvoice && !isSummaryOnly

  return {
    isSummaryOnly,
    isReconciled
  }
}

function InvoiceCard({ invoice, onView, onEdit, onDelete }: {
  invoice: MedicalInvoice
  onView: (invoice: MedicalInvoice) => void
  onEdit: (invoice: MedicalInvoice) => void
  onDelete: (invoice: MedicalInvoice) => void
}) {
  const specialtyColor = getSpecialtyColor(invoice.specialty)
  const specialtyIcon = getSpecialtyIcon(invoice.specialty)
  const paymentColor = getPaymentStatusColor(invoice.is_paid)
  const reimbursedAmount = getInvoiceReimbursedAmount(invoice)
  const patientFinalCharge = getInvoiceRemainingPatientCharge(invoice)
  const outstandingAmount = getInvoiceOutstandingAmount(invoice)
  const reimbursementRate = calculateReimbursementRate(invoice.total_amount, reimbursedAmount)
  const primaryFacts = getInvoicePrimaryFacts(invoice)
  const sourceState = getInvoiceSourceState(invoice)

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="card hover:border-primary-800/50 transition-colors"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-4 flex-1">
          <div className="w-12 h-12 rounded-xl bg-dark-surface-high border border-dark-border flex items-center justify-center text-xl">
            {specialtyIcon}
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-3 mb-2">
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-white truncate">
                  {invoice.practitioner_name}
                </h3>
                <p className="text-sm text-slate-300 mt-1">{primaryFacts.join(' · ')}</p>
              </div>

              <div className="flex items-center gap-2 flex-shrink-0">
                {sourceState.isSummaryOnly ? (
                  <span className="badge border text-xs border-slate-500/20 bg-slate-500/10 text-slate-200">
                    Récap
                  </span>
                ) : (
                  <span className="badge border text-xs border-cyan-500/20 bg-cyan-500/10 text-cyan-100">
                    Facture
                  </span>
                )}
                {sourceState.isReconciled && (
                  <span className="badge border text-xs border-emerald-500/20 bg-emerald-500/10 text-emerald-200">
                    Rapprochée
                  </span>
                )}
                <span className={`badge border text-xs ${specialtyColor}`}>
                  {getSpecialtyLabel(invoice.specialty)}
                </span>
                <span className={`badge border text-xs ${paymentColor}`}>
                  {getPaymentStatusLabel(invoice.is_paid)}
                </span>
              </div>
            </div>

            <div className="mt-4 flex flex-wrap gap-3 text-sm">
              <div className="rounded-lg border border-dark-border bg-dark-surface-high px-3 py-2">
                <p className="text-xs text-slate-500">Total</p>
                <p className="font-semibold text-white">{formatCurrency(invoice.total_amount)}</p>
              </div>

              <div className="rounded-lg border border-dark-border bg-dark-surface-high px-3 py-2">
                <p className="text-xs text-slate-500">Remboursé</p>
                <p className="font-semibold text-green-400">
                  {formatCurrency(reimbursedAmount)}
                </p>
              </div>

              <div className="rounded-lg border border-dark-border bg-dark-surface-high px-3 py-2">
                <p className="text-xs text-slate-500">Payé / avancé</p>
                <p className="font-semibold text-blue-300">{formatCurrency(invoice.patient_paid_amount || 0)}</p>
              </div>

              <div className="rounded-lg border border-dark-border bg-dark-surface-high px-3 py-2">
                <p className="text-xs text-slate-500">Reste final</p>
                <p className="font-semibold text-amber-300">{formatCurrency(patientFinalCharge)}</p>
              </div>

              <div className="rounded-lg border border-dark-border bg-dark-surface-high px-3 py-2">
                <p className="text-xs text-slate-500">Taux</p>
                <p className={`font-semibold ${reimbursementRate >= 70 ? 'text-green-400' : reimbursementRate >= 50 ? 'text-yellow-400' : 'text-red-400'}`}>
                  {reimbursementRate}%
                </p>
              </div>
            </div>

            {outstandingAmount > 0.009 && (
              <p className="mt-3 text-xs text-slate-400">
                Reste à payer au praticien :{' '}
                <span className="font-medium text-slate-200">{formatCurrency(outstandingAmount)}</span>
              </p>
            )}

            {invoice.notes && (
              <div className="mt-3 p-3 rounded-lg bg-dark-surface-high border border-dark-border">
                <p className="text-sm text-slate-300">{invoice.notes}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between mt-6 pt-4 border-t border-dark-border">
        <div className="text-xs text-slate-500">
          {invoice.invoice_number && `Facture ${invoice.invoice_number} • `}
          Ajouté le {format(new Date(invoice.created_at), 'd MMM yyyy', { locale: fr })}
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => onView(invoice)}
            className="btn-ghost text-sm"
          >
            <Eye className="w-4 h-4" />
            Voir
          </button>

          <button
            onClick={() => onEdit(invoice)}
            className="btn-ghost text-sm"
          >
            <Edit className="w-4 h-4" />
            Modifier
          </button>

          <button
            onClick={() => onDelete(invoice)}
            className="btn-ghost text-sm text-red-400 hover:text-red-300"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </motion.div>
  )
}

function InvoiceForm({
  invoice,
  onSubmit,
  onCancel
}: {
  invoice?: MedicalInvoice | null
  onSubmit: (data: InvoiceFormData) => void
  onCancel: () => void
}) {
  const [formData, setFormData] = useState<InvoiceFormData>(
    invoice ? {
      practitioner_name: invoice.practitioner_name,
      medical_facility: invoice.medical_facility || '',
      specialty: invoice.specialty,
      service_date: invoice.service_date,
      invoice_date: invoice.invoice_date,
      total_amount: invoice.total_amount,
      patient_paid_amount: invoice.patient_paid_amount,
      is_paid: invoice.is_paid,
      payment_date: invoice.payment_date || '',
      secu_reimbursed: invoice.secu_reimbursed || 0,
      mutuelle_reimbursed: invoice.mutuelle_reimbursed || 0,
      invoice_number: invoice.invoice_number || '',
      notes: invoice.notes || '',
    } : initialFormData
  )

  const [errors, setErrors] = useState<string[]>([])
  const totalReimbursed = getInvoiceReimbursedAmount(formData)
  const finalPatientCharge = getInvoiceRemainingPatientCharge(formData)
  const outstandingAmount = getInvoiceOutstandingAmount(formData)

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    const validation = validateInvoiceInput({
      patient_id: '', // Sera ajouté dans le composant parent
      ...formData
    })

    if (!validation.isValid) {
      setErrors(validation.errors)
      return
    }

    setErrors([])
    onSubmit(formData)
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onCancel}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="bg-dark-surface border border-dark-border rounded-2xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-xl font-semibold text-white mb-6">
          {invoice ? 'Modifier la facture' : 'Nouvelle facture'}
        </h2>

        {errors.length > 0 && (
          <div className="mb-4 p-3 rounded-lg bg-red-900/20 border border-red-800/40">
            <ul className="text-sm text-red-300 space-y-1">
              {errors.map((error, index) => (
                <li key={index}>• {error}</li>
              ))}
            </ul>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-slate-300 mb-2">Praticien *</label>
              <input
                type="text"
                value={formData.practitioner_name}
                onChange={(e) => setFormData(prev => ({ ...prev, practitioner_name: e.target.value }))}
                className="input"
                placeholder="Dr. Martin"
                required
              />
            </div>

            <div>
              <label className="block text-sm text-slate-300 mb-2">Établissement</label>
              <input
                type="text"
                value={formData.medical_facility}
                onChange={(e) => setFormData(prev => ({ ...prev, medical_facility: e.target.value }))}
                className="input"
                placeholder="Hôpital Saint-Louis"
              />
            </div>

            <div>
              <label className="block text-sm text-slate-300 mb-2">Spécialité *</label>
              <select
                value={formData.specialty}
                onChange={(e) => setFormData(prev => ({ ...prev, specialty: e.target.value as MedicalSpecialty }))}
                className="input"
                required
              >
                {specialties.map(specialty => (
                  <option key={specialty.value} value={specialty.value}>{specialty.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm text-slate-300 mb-2">Date de service *</label>
              <input
                type="date"
                value={formData.service_date}
                onChange={(e) => setFormData(prev => ({ ...prev, service_date: e.target.value }))}
                className="input"
                required
              />
            </div>

            <div>
              <label className="block text-sm text-slate-300 mb-2">Date de facture *</label>
              <input
                type="date"
                value={formData.invoice_date}
                onChange={(e) => setFormData(prev => ({ ...prev, invoice_date: e.target.value }))}
                className="input"
                required
              />
            </div>

            <div>
              <label className="block text-sm text-slate-300 mb-2">Numéro de facture</label>
              <input
                type="text"
                value={formData.invoice_number}
                onChange={(e) => setFormData(prev => ({ ...prev, invoice_number: e.target.value }))}
                className="input"
                placeholder="F2024-001"
              />
            </div>

            <div>
              <label className="block text-sm text-slate-300 mb-2">Montant total *</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={formData.total_amount}
                onChange={(e) => setFormData(prev => ({ ...prev, total_amount: parseFloat(e.target.value) || 0 }))}
                className="input"
                placeholder="25.00"
                required
              />
            </div>

            <div>
              <label className="block text-sm text-slate-300 mb-2">Montant payé / avancé</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={formData.patient_paid_amount}
                onChange={(e) => setFormData(prev => ({ ...prev, patient_paid_amount: parseFloat(e.target.value) || 0 }))}
                className="input"
                placeholder="25.00"
              />
            </div>

            <div>
              <label className="block text-sm text-slate-300 mb-2">Remboursement Sécurité Sociale</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={formData.secu_reimbursed}
                onChange={(e) => setFormData(prev => ({ ...prev, secu_reimbursed: parseFloat(e.target.value) || 0 }))}
                className="input"
                placeholder="17.50"
              />
            </div>

            <div>
              <label className="block text-sm text-slate-300 mb-2">Remboursement Mutuelle</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={formData.mutuelle_reimbursed}
                onChange={(e) => setFormData(prev => ({ ...prev, mutuelle_reimbursed: parseFloat(e.target.value) || 0 }))}
                className="input"
                placeholder="7.50"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3 rounded-xl border border-dark-border bg-dark-surface-high p-3 md:grid-cols-3">
            <div>
              <p className="text-xs text-slate-500">Payé / avancé</p>
              <p className="text-sm font-semibold text-blue-300">{formatCurrency(formData.patient_paid_amount || 0)}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500">Remboursé</p>
              <p className="text-sm font-semibold text-green-400">{formatCurrency(totalReimbursed)}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500">Reste final estimé</p>
              <p className="text-sm font-semibold text-amber-300">{formatCurrency(finalPatientCharge)}</p>
            </div>
            {outstandingAmount > 0.009 && (
              <div className="md:col-span-3">
                <p className="text-xs text-slate-400">
                  Il reste {formatCurrency(outstandingAmount)} à régler au praticien si ce montant n’a pas encore été payé.
                </p>
              </div>
            )}
          </div>

          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.is_paid}
                onChange={(e) => setFormData(prev => ({ ...prev, is_paid: e.target.checked }))}
                className="rounded border-dark-border bg-dark-surface"
              />
              <span className="text-sm text-slate-300">Facture payée</span>
            </label>

            {formData.is_paid && (
              <div>
                <label className="block text-sm text-slate-300 mb-1">Date de paiement</label>
                <input
                  type="date"
                  value={formData.payment_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, payment_date: e.target.value }))}
                  className="input"
                />
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm text-slate-300 mb-2">Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              className="textarea"
              placeholder="Notes personnelles sur cette facture..."
              rows={3}
            />
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onCancel}
              className="btn-ghost"
            >
              Annuler
            </button>
            <button
              type="submit"
              className="btn-primary"
            >
              {invoice ? 'Modifier' : 'Ajouter'}
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  )
}

function InvoiceUpload({ onUpload }: { onUpload: (files: File[]) => void }) {
  const [files, setFiles] = useState<File[]>([])

  const onDrop = useCallback((acceptedFiles: File[]) => {
    setFiles(acceptedFiles)
  }, [])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'image/*': ['.png', '.jpg', '.jpeg', '.gif', '.bmp', '.tiff']
    },
    maxSize: 50 * 1024 * 1024, // 50MB
    multiple: true
  })

  const handleUpload = () => {
    if (files.length === 0) {
      toast.error('Veuillez sélectionner au moins un fichier')
      return
    }
    onUpload(files)
    setFiles([])
  }

  return (
    <div className="space-y-6">
      <div
        {...getRootProps()}
        className={`border-2 border-dashed rounded-2xl p-8 text-center transition-colors cursor-pointer ${
          isDragActive
            ? 'border-primary-400 bg-primary-900/10'
            : 'border-dark-border hover:border-primary-600'
        }`}
      >
        <input {...getInputProps()} />
        <Upload className="w-12 h-12 text-slate-400 mx-auto mb-4" />
        {isDragActive ? (
          <p className="text-primary-300">Déposez vos factures ici...</p>
        ) : (
          <div>
            <p className="text-white mb-2">
              Glissez vos factures médicales ici ou cliquez pour sélectionner
            </p>
            <p className="text-sm text-slate-400">
              PDF et images acceptés (max 50MB par fichier)
            </p>
          </div>
        )}
      </div>

      {files.length > 0 && (
        <div className="space-y-3">
          <h3 className="font-medium text-white">Fichiers sélectionnés:</h3>
          {files.map((file, index) => (
            <div key={index} className="flex items-center justify-between p-3 rounded-lg bg-dark-surface-high border border-dark-border">
              <div className="flex items-center gap-3">
                <FileText className="w-5 h-5 text-slate-400" />
                <div>
                  <p className="text-sm text-white">{file.name}</p>
                  <p className="text-xs text-slate-400">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                </div>
              </div>
              <button
                onClick={() => setFiles(files.filter((_, i) => i !== index))}
                className="text-red-400 hover:text-red-300"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}

          <div className="flex justify-end gap-3">
            <button
              onClick={() => setFiles([])}
              className="btn-ghost"
            >
              Annuler
            </button>
            <button
              onClick={handleUpload}
              className="btn-primary"
            >
              <Upload className="w-4 h-4" />
              Traiter les factures
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default function Invoices() {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const [activeTab, setActiveTab] = useState<'list' | 'stats' | 'upload' | 'reimbursements'>('list')
  const [searchTerm, setSearchTerm] = useState('')
  const [filterSpecialty, setFilterSpecialty] = useState<MedicalSpecialty | ''>('')
  const [filterPaid, setFilterPaid] = useState<boolean | ''>('')
  const [showForm, setShowForm] = useState(false)
  const [editingInvoice, setEditingInvoice] = useState<MedicalInvoice | null>(null)
  const [viewingInvoice, setViewingInvoice] = useState<MedicalInvoice | null>(null)

  // Queries
  const { data: invoices = [], isLoading } = useQuery({
    queryKey: ['medical-invoices', user?.id, searchTerm, filterSpecialty, filterPaid],
    queryFn: async () => {
      if (searchTerm) {
        return searchMedicalInvoices(user!.id, searchTerm, {
          specialty: filterSpecialty || undefined,
          isPaid: filterPaid === '' ? undefined : filterPaid
        })
      }
      return getMedicalInvoices(user!.id, filterSpecialty || undefined)
    },
    enabled: Boolean(user),
  })

  const { data: expensesBySpecialty = [] } = useQuery({
    queryKey: ['expenses-by-specialty', user?.id],
    queryFn: () => getExpensesBySpecialty(user!.id),
    enabled: Boolean(user) && activeTab === 'stats',
  })

  const { data: monthlyExpenses = [] } = useQuery({
    queryKey: ['monthly-expenses', user?.id],
    queryFn: () => getMonthlyExpenses(user!.id, new Date().getFullYear()),
    enabled: Boolean(user) && activeTab === 'stats',
  })

  const { data: lifetimeStats } = useQuery({
    queryKey: ['lifetime-expenses', user?.id],
    queryFn: () => getTotalLifetimeExpenses(user!.id),
    enabled: Boolean(user),
  })

  const { data: unpaidInvoices = [] } = useQuery({
    queryKey: ['unpaid-invoices', user?.id],
    queryFn: () => getUnpaidInvoices(user!.id),
    enabled: Boolean(user),
  })

  const refreshInvoiceQueries = () => {
    queryClient.invalidateQueries({ queryKey: ['medical-invoices', user?.id] })
    queryClient.invalidateQueries({ queryKey: ['lifetime-expenses', user?.id] })
    queryClient.invalidateQueries({ queryKey: ['expenses-by-specialty', user?.id] })
    queryClient.invalidateQueries({ queryKey: ['monthly-expenses', user?.id] })
    queryClient.invalidateQueries({ queryKey: ['unpaid-invoices', user?.id] })
  }

  // Mutations
  const createMutation = useMutation({
    mutationFn: async (data: InvoiceFormData) => {
      const input: MedicalInvoiceInput = {
        patient_id: user!.id,
        ...data
      }
      return createMedicalInvoice(input)
    },
    onSuccess: () => {
      toast.success('Facture ajoutée avec succès')
      refreshInvoiceQueries()
      setShowForm(false)
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Erreur lors de l\'ajout')
    }
  })

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: InvoiceFormData }) => {
      return updateMedicalInvoice(id, data)
    },
    onSuccess: () => {
      toast.success('Facture modifiée avec succès')
      refreshInvoiceQueries()
      setShowForm(false)
      setEditingInvoice(null)
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Erreur lors de la modification')
    }
  })

  const deleteMutation = useMutation({
    mutationFn: (invoiceId: string) => deleteMedicalInvoice(invoiceId),
    onSuccess: () => {
      toast.success('Facture supprimée')
      refreshInvoiceQueries()
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Erreur lors de la suppression')
    }
  })

  const uploadMutation = useMutation({
    mutationFn: async (files: File[]) => {
      const results = []
      for (const file of files) {
        try {
          const extractedData = await extractInvoiceDataFromPDF(file)
          const input: MedicalInvoiceInput = {
            patient_id: user!.id,
            practitioner_name: extractedData.practitioner_name || 'Praticien non identifié',
            total_amount: extractedData.total_amount || 0,
            specialty: extractedData.specialty || 'other',
            service_date: extractedData.service_date || new Date().toISOString().split('T')[0],
            invoice_date: extractedData.invoice_date || new Date().toISOString().split('T')[0],
            medical_facility: extractedData.medical_facility || '',
            notes: `Facture extraite automatiquement de ${file.name}`,
            metadata: {
              import_origin: 'document',
              source_file_name: file.name
            }
          }
          const { invoice } = await saveMedicalInvoiceWithReconciliation(input, {
            origin: 'document',
            sourceFileName: file.name
          })
          results.push(invoice)
        } catch (error) {
          console.error('Erreur traitement fichier:', file.name, error)
          throw new Error(`Erreur lors du traitement de ${file.name}`)
        }
      }
      return results
    },
    onSuccess: (results) => {
      toast.success(`${results.length} facture(s) ajoutée(s)`)
      refreshInvoiceQueries()
      setActiveTab('list')
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Erreur lors de l\'import')
    }
  })

  const handleEdit = (invoice: MedicalInvoice) => {
    setEditingInvoice(invoice)
    setShowForm(true)
  }

  const handleDelete = (invoice: MedicalInvoice) => {
    if (window.confirm(`Êtes-vous sûr de vouloir supprimer cette facture de Dr. ${invoice.practitioner_name} ?`)) {
      deleteMutation.mutate(invoice.id)
    }
  }

  const handleSubmitForm = (data: InvoiceFormData) => {
    if (editingInvoice) {
      updateMutation.mutate({ id: editingInvoice.id, data })
    } else {
      createMutation.mutate(data)
    }
  }

  const handleUpload = (files: File[]) => {
    uploadMutation.mutate(files)
  }

  const handleSummaryImported = () => {
    refreshInvoiceQueries()
    setActiveTab('list')
  }

  const totalPatientPaid = invoices.reduce((sum, invoice) => sum + (invoice.patient_paid_amount || 0), 0)

  // Préparation des données pour les graphiques
  const pieChartData = expensesBySpecialty.map((item, index) => ({
    name: getSpecialtyLabel(item.specialty),
    value: item.total_patient_charge || 0,
    color: CHART_COLORS[index % CHART_COLORS.length]
  }))

  const barChartData = monthlyExpenses
    .slice(-6) // 6 derniers mois
    .map(item => ({
      month: format(new Date(item.month), 'MMM yyyy', { locale: fr }),
      total: item.total_amount || 0,
      rembourse: (item.total_secu_reimbursed || 0) + (item.total_mutuelle_reimbursed || 0),
      reste: item.total_patient_charge || 0
    }))

  const invoiceQuickFacts = [
    { label: 'Total', value: formatCurrency(lifetimeStats?.total_amount || 0) },
    { label: 'Payé / avancé', value: formatCurrency(totalPatientPaid) },
    { label: 'Remboursé', value: formatCurrency(lifetimeStats?.total_reimbursed || 0) },
    { label: 'Reste final', value: formatCurrency(lifetimeStats?.total_patient_charge || 0) },
    { label: 'Impayées', value: `${unpaidInvoices.length}` }
  ]

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Factures médicales</h1>
          <p className="text-sm text-slate-400 mt-1">Vue simple des frais, remboursements et justificatifs</p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {invoiceQuickFacts.map((fact) => (
          <div
            key={fact.label}
            className="rounded-full border border-dark-border bg-dark-surface-high px-3 py-1.5 text-sm text-slate-200"
          >
            <span className="font-semibold text-white">{fact.value}</span> {fact.label}
          </div>
        ))}
      </div>

      {/* Onglets */}
      <div className="flex space-x-1 bg-dark-surface-high rounded-lg p-1">
        <button
          onClick={() => setActiveTab('list')}
          className={`px-4 py-2 rounded-md transition-colors text-sm ${
            activeTab === 'list'
              ? 'bg-primary-600 text-white'
              : 'text-slate-400 hover:text-white'
          }`}
        >
          Factures
        </button>
        <button
          onClick={() => setActiveTab('stats')}
          className={`px-4 py-2 rounded-md transition-colors text-sm ${
            activeTab === 'stats'
              ? 'bg-primary-600 text-white'
              : 'text-slate-400 hover:text-white'
          }`}
        >
          Stats
        </button>
        <button
          onClick={() => setActiveTab('upload')}
          className={`px-4 py-2 rounded-md transition-colors text-sm ${
            activeTab === 'upload'
              ? 'bg-primary-600 text-white'
              : 'text-slate-400 hover:text-white'
          }`}
        >
          Importer
        </button>
        <button
          onClick={() => setActiveTab('reimbursements')}
          className={`px-4 py-2 rounded-md transition-colors text-sm ${
            activeTab === 'reimbursements'
              ? 'bg-primary-600 text-white'
              : 'text-slate-400 hover:text-white'
          }`}
        >
          Remboursements
        </button>
      </div>

      {/* Contenu des onglets */}
      {activeTab === 'list' && (
        <div className="space-y-6">
          {/* Contrôles */}
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input pl-10"
                placeholder="Rechercher praticien, établissement..."
              />
            </div>

            <select
              value={filterSpecialty}
              onChange={(e) => setFilterSpecialty(e.target.value as MedicalSpecialty | '')}
              className="input lg:w-64"
            >
              <option value="">Toutes spécialités</option>
              {specialties.map(specialty => (
                <option key={specialty.value} value={specialty.value}>{specialty.label}</option>
              ))}
            </select>

            <select
              value={filterPaid === '' ? '' : filterPaid.toString()}
              onChange={(e) => setFilterPaid(e.target.value === '' ? '' : e.target.value === 'true')}
              className="input lg:w-48"
            >
              <option value="">Toutes factures</option>
              <option value="true">Payées</option>
              <option value="false">En attente</option>
            </select>

            <button
              onClick={() => {
                setEditingInvoice(null)
                setShowForm(true)
              }}
              className="btn-primary"
            >
              <Plus className="w-4 h-4" />
              Nouvelle facture
            </button>
          </div>

          {/* Liste des factures */}
          {isLoading ? (
            <div className="card text-center py-8">
              <div className="spinner mx-auto" />
              <p className="text-slate-400 mt-4">Chargement des factures...</p>
            </div>
          ) : invoices.length === 0 ? (
            <div className="card text-center py-12">
              <CreditCard className="w-16 h-16 text-slate-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">
                {searchTerm || filterSpecialty || filterPaid !== ''
                  ? 'Aucune facture trouvée'
                  : 'Aucune facture médicale'
                }
              </h3>
              <p className="text-slate-400 max-w-md mx-auto mb-6">
                {searchTerm || filterSpecialty || filterPaid !== ''
                  ? 'Essayez de modifier vos critères de recherche'
                  : 'Ajoutez vos premières factures médicales pour commencer le suivi financier'
                }
              </p>
              {!searchTerm && !filterSpecialty && filterPaid === '' && (
                <button
                  onClick={() => {
                    setEditingInvoice(null)
                    setShowForm(true)
                  }}
                  className="btn-primary"
                >
                  <Plus className="w-4 h-4" />
                  Ajouter une facture
                </button>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {invoices.map((invoice) => (
                <InvoiceCard
                  key={invoice.id}
                  invoice={invoice}
                  onView={setViewingInvoice}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'stats' && (
        <div className="space-y-6">
          {/* Graphiques */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Répartition par spécialité */}
            <div className="card">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <PieChart className="w-5 h-5" />
                Dépenses par spécialité
              </h3>
              {pieChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <RechartsPieChart>
                    <Pie
                      data={pieChartData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    >
                      {pieChartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value) => [formatCurrency(Number(value)), 'Reste final']}
                      labelStyle={{ color: '#000' }}
                    />
                  </RechartsPieChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-slate-400 text-center py-8">Aucune donnée disponible</p>
              )}
            </div>

            {/* Évolution mensuelle */}
            <div className="card">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <BarChart3 className="w-5 h-5" />
                Évolution des 6 derniers mois
              </h3>
              {barChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={barChartData}>
                    <XAxis dataKey="month" stroke="#94a3b8" />
                    <YAxis stroke="#94a3b8" />
                    <Tooltip
                      formatter={(value, name) => [
                        formatCurrency(Number(value)),
                        name === 'total' ? 'Total' : name === 'rembourse' ? 'Remboursé' : 'Reste final'
                      ]}
                      labelStyle={{ color: '#000' }}
                    />
                    <Legend />
                    <Bar dataKey="total" fill="#3B82F6" name="Total" />
                    <Bar dataKey="rembourse" fill="#10B981" name="Remboursé" />
                    <Bar dataKey="reste" fill="#F59E0B" name="Reste final" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-slate-400 text-center py-8">Aucune donnée disponible</p>
              )}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'upload' && (
        <div className="space-y-6">
          <div className="card">
            <h3 className="text-lg font-semibold text-white mb-4">Factures PDF</h3>
            <p className="text-slate-300 mb-6">
              Import des vraies factures. Si une ligne du récapitulatif existe déjà, elle sera rapprochée automatiquement.
            </p>
            <InvoiceUpload onUpload={handleUpload} />
          </div>

          <div className="card">
            <h3 className="text-lg font-semibold text-white mb-4">Récapitulatif Excel / CSV</h3>
            <p className="text-slate-300 mb-6">
              Importez un tableau de dépenses déjà préparé. Les lignes iront dans `Factures` et se recouperont plus tard avec les pièces réelles.
            </p>
            {user && (
              <ExpenseSummaryImporter
                patientId={user.id}
                onImported={handleSummaryImported}
              />
            )}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="card border-amber-800/40 bg-amber-900/10"
          >
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-400 mt-0.5" />
              <div>
                <h3 className="font-semibold text-amber-300">Rapprochement automatique</h3>
                <p className="text-sm text-amber-200/80 mt-1">
                  Le système compare la date, le montant, le praticien et le numéro de facture quand il existe pour éviter les doublons.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {activeTab === 'reimbursements' && (
        <div className="space-y-6">
          <div className="card">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-white">Suivi des Remboursements</h2>
                <p className="text-sm text-slate-400 mt-1">
                  Suivez vos demandes de remboursement et leurs statuts en temps réel
                </p>
              </div>
            </div>

            <ReimbursementTracker />
          </div>
        </div>
      )}

      {/* Formulaire d'ajout/édition */}
      {showForm && (
        <InvoiceForm
          invoice={editingInvoice}
          onSubmit={handleSubmitForm}
          onCancel={() => {
            setShowForm(false)
            setEditingInvoice(null)
          }}
        />
      )}

      {/* Modal de visualisation */}
      {viewingInvoice && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setViewingInvoice(null)}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-dark-surface border border-dark-border rounded-2xl p-6 w-full max-w-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-6">
              <div>
                <h2 className="text-xl font-semibold text-white">
                  Facture Dr. {viewingInvoice.practitioner_name}
                </h2>
                <p className="text-slate-400">
                  {format(new Date(viewingInvoice.service_date), 'd MMMM yyyy', { locale: fr })}
                </p>
              </div>
              <button
                onClick={() => setViewingInvoice(null)}
                className="btn-ghost"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-slate-400">Spécialité</label>
                  <p className="text-white">{getSpecialtyLabel(viewingInvoice.specialty)}</p>
                </div>

                {viewingInvoice.medical_facility && (
                  <div>
                    <label className="text-sm text-slate-400">Établissement</label>
                    <p className="text-white">{viewingInvoice.medical_facility}</p>
                  </div>
                )}

                <div>
                  <label className="text-sm text-slate-400">Montant total</label>
                  <p className="text-white font-semibold">{formatCurrency(viewingInvoice.total_amount)}</p>
                </div>

                <div>
                  <label className="text-sm text-slate-400">Statut</label>
                  <span className={`badge border ${getPaymentStatusColor(viewingInvoice.is_paid)}`}>
                    {getPaymentStatusLabel(viewingInvoice.is_paid)}
                  </span>
                </div>

                <div>
                  <label className="text-sm text-slate-400">Payé / avancé</label>
                  <p className="text-blue-300 font-semibold">{formatCurrency(viewingInvoice.patient_paid_amount || 0)}</p>
                </div>

                <div>
                  <label className="text-sm text-slate-400">Reste final</label>
                  <p className="text-amber-300 font-semibold">
                    {formatCurrency(getInvoiceRemainingPatientCharge(viewingInvoice))}
                  </p>
                </div>

                {viewingInvoice.secu_reimbursed !== null && viewingInvoice.secu_reimbursed > 0 && (
                  <div>
                    <label className="text-sm text-slate-400">Sécu remboursé</label>
                    <p className="text-green-400 font-semibold">{formatCurrency(viewingInvoice.secu_reimbursed)}</p>
                  </div>
                )}

                {viewingInvoice.mutuelle_reimbursed !== null && viewingInvoice.mutuelle_reimbursed > 0 && (
                  <div>
                    <label className="text-sm text-slate-400">Mutuelle remboursé</label>
                    <p className="text-green-400 font-semibold">{formatCurrency(viewingInvoice.mutuelle_reimbursed)}</p>
                  </div>
                )}
              </div>

              {viewingInvoice.notes && (
                <div>
                  <label className="text-sm text-slate-400 block mb-1">Notes</label>
                  <p className="text-white">{viewingInvoice.notes}</p>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </div>
  )
}
